import React, { useEffect, useRef, useState } from "react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import NotificationPopup from "@/components/NotificationPopup";
import BottomNav from "@/components/BottomNav";
import GenieLauncher from "@/components/GenieLauncher";
import { useAuth } from "@/context/AuthContext";
import { useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MailCheck, X, Loader2 } from "lucide-react";

function VerifyBanner() {
  const { user, refresh } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [otp, setOtp] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!user || user.email_verified !== false || dismissed) return null;

  const send = async () => {
    try { await api.post("/auth/send-verification", { email: user.email }); toast.success("Code sent to your email"); setOpen(true); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  const verify = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/verify-email", { email: user.email, otp });
      toast.success("Email verified");
      setDismissed(true);
      await refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Invalid code"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border-b border-lime/20 bg-lime/10" data-testid="verify-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex flex-wrap items-center gap-3 text-xs font-mono">
        <MailCheck className="w-4 h-4 text-lime flex-shrink-0" />
        <span className="text-lime uppercase tracking-widest">Verify your email —</span>
        <span className="text-text/80 flex-1 min-w-0">Come, complete your profile and enjoy 😉</span>
        {!open ? (
          <button onClick={send} data-testid="verify-banner-send" className="px-3 py-1 rounded-full bg-lime text-bg font-bold uppercase text-[10px] tracking-widest">Send code</button>
        ) : (
          <form onSubmit={verify} className="flex items-center gap-2">
            <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g,"").slice(0,6))} maxLength={6} inputMode="numeric" placeholder="000000" className="w-24 px-2 py-1 rounded bg-bg border border-white/10 text-center tracking-widest" data-testid="verify-banner-otp" />
            <button disabled={busy || otp.length !== 6} data-testid="verify-banner-submit" className="px-3 py-1 rounded-full bg-lime text-bg font-bold uppercase text-[10px] tracking-widest disabled:opacity-50">
              {busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Verify"}
            </button>
          </form>
        )}
        <button onClick={() => setDismissed(true)} data-testid="verify-banner-close" className="icon-circle !w-6 !h-6 bg-white/5"><X className="w-3 h-3" /></button>
      </div>
    </div>
  );
}

/** Desktop-only soft violet radial glow that follows the cursor. */
function CursorGlow() {
  const ref = useRef(null);
  useEffect(() => {
    if (!window.matchMedia("(hover: hover)").matches) return;
    const move = (e) => {
      if (ref.current) { ref.current.style.left = `${e.clientX}px`; ref.current.style.top = `${e.clientY}px`; }
    };
    window.addEventListener("mousemove", move, { passive: true });
    return () => window.removeEventListener("mousemove", move);
  }, []);
  return <div ref={ref} className="cursor-glow hidden md:block" aria-hidden />;
}

/** Fade-and-slide-up sections as they scroll into view. */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll("main section");
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add("in-view"); io.unobserve(en.target); } });
    }, { threshold: 0.08 });
    els.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight) { el.classList.add("in-view"); return; }
      el.classList.add("reveal-scroll");
      io.observe(el);
    });
    return () => io.disconnect();
  }, []);
}

const PAGE_TITLES = {
  "/": "Eventom Online — Every Event. Every Crowd. One Platform | Book Event Tickets India",
  "/about": "About Eventom — Building the Live Culture of Bharat",
  "/ai-gallery": "AI Event Photos — Find Your Photos | Eventom",
  "/ai-photos": "Eventom AI Camera — Find Your Event Photos",
  "/vip": "Eventom Club VIP — All-Access ₹99 | Eventom",
  "/contests": "Contests & Giveaways | Eventom",
  "/leaderboard": "Squad Leaderboard — Referral Rankings | Eventom",
  "/organisers": "List Your Event — For Organisers | Eventom",
  "/brands": "Sponsor Events & Brand Activations | Eventom",
  "/brands-portal": "Brand Partnerships Portal | Eventom",
  "/ecosystem": "Ecosystem — Artists, Creators & Vendors | Eventom",
  "/work-with-eventom": "Work With Eventom — Careers & Collabs",
  "/chat": "Ask Eventom Genie — AI Event Search",
  "/login": "Log In | Eventom",
  "/signup": "Sign Up — Discover Events Near You | Eventom",
};

export default function Layout({ children }) {
  useScrollReveal();
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (pathname.startsWith("/city/")) {
      const c = decodeURIComponent(pathname.split("/city/")[1] || "").split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
      document.title = `Events in ${c} — Book Tickets & Experiences | Eventom`;
    } else {
      document.title = PAGE_TITLES[pathname] || "Eventom Online — Events, Tickets & Experiences in India";
    }
  }, [pathname]);
  return (
    <div className="min-h-screen flex flex-col bg-bg text-text relative">
      <CursorGlow />
      <Nav />
      <VerifyBanner />
      <main key={pathname} className="flex-1 relative z-[2] page-fade">{children}</main>
      <Footer />
      <PWAInstallPrompt />
      <NotificationPopup />
      <GenieLauncher />
      <BottomNav />
    </div>
  );
}
